import type { MaybeComputedElementRef } from '@vueuse/core'
import type { MaybeRefOrGetter } from 'vue'
import type { GtmCustomerType } from '~/composables/useGtmUserProperties'
import type { CartItem } from '~/utils/easyApi'
import type { Ga4CartItem, Ga4CartItemInput } from '~/utils/helper'
import { resolvePageSection } from '~/composables/usePageSection'

// Module-scoped dedupe set so all CartItem instances on the same page share
// the same `fired` registry. Reset on cart page mount via resetGpoViewDedupe().
const firedGpoViews = new Set<string>()

interface CartLike {
  supplier?: { name?: string, handle?: string }
  meta?: { voucherCode?: string } & Record<string, any>
  items: Ga4CartItemInput[]
}

interface GpoRecommendationsPayload {
  gpoName: string
  itemId: string
  itemName: string
  supplierName: string
  supplierHandle: string
}

export interface AddToCartTrackingCtx {
  index?: number
  pageSection?: string
  itemListId?: string
  itemListName?: string
}

interface SuggestionTrackingCtx {
  index?: number
}

interface BeginCheckoutCtx {
  checkoutType: 'single' | 'all'
  pageSection?: string
}

interface PurchaseCtx {
  transactionId: string
  customerType: GtmCustomerType
  coupon?: string
}

interface GpoRecommendationsBindings {
  supplierHandle: MaybeRefOrGetter<string>
  itemId: MaybeRefOrGetter<string>
  itemName: MaybeRefOrGetter<string>
}

interface GpoRecommendationsViewBindings extends GpoRecommendationsBindings {
  hasAlternativeProducts: MaybeRefOrGetter<boolean | undefined>
  matchingClinicChain: MaybeRefOrGetter<unknown | null | undefined>
}

/**
 * Centralized GA4 tracking for cart-funnel events (add/remove/view cart,
 * GPO recommendations, suggestion, summary expand, begin_checkout, purchase).
 */
export function useCartTracking() {
  const gtm = useGtm()
  const log = useLogger('useCartTracking')
  const { $i18n } = useNuxtApp()
  const { convertCartItemToGa4EcommerceItem } = useHelper()
  const { isItemGpoCompliant, userClinicChainName } = useGpoCompliance()
  const { calculateCartTotal, getItemPrice } = usePriceUtils()
  const { getTotalFreightCost } = useFreightProgress()
  const cartStore = useCart()
  const { track: trackProductFruits } = useProductFruits()

  const route = useRoute()
  const { getOriginSection } = useCartOriginSection()

  function currentPageSection() {
    return resolvePageSection(route.path)
  }

  function gpoContext() {
    // Ga4CartItemInput omits `clinicChains`; cart items still carry it at runtime.
    return {
      $i18n,
      isGpoCompliant: (item: Ga4CartItemInput) => isItemGpoCompliant(item as CartItem),
    }
  }

  function trackAddToCart(
    item: Ga4CartItemInput,
    supplier: { name?: string },
    ctx: AddToCartTrackingCtx = {},
  ) {
    const index = ctx.index ?? 1
    const ga4Item = convertCartItemToGa4EcommerceItem(item, supplier.name ?? '', index, gpoContext())
    ga4Item.price = getItemPrice(item as unknown as Parameters<typeof getItemPrice>[0]) / 100
    if (ctx.itemListId) ga4Item.item_list_id = ctx.itemListId
    if (ctx.itemListName) ga4Item.item_list_name = ctx.itemListName
    const value = Math.round((ga4Item.price ?? 0) * (ga4Item.quantity ?? 0) * 100) / 100

    const pageSection = ctx.pageSection || currentPageSection()

    if (import.meta.dev && !pageSection) {
      log.warn('add_to_cart fired with an empty page_section', { path: route.path, ctx })
    }

    gtm?.trackEvent({ ecommerce: null })
    gtm?.trackEvent({
      event: 'add_to_cart',
      page_section: pageSection,
      ...(ctx.itemListId && { item_list_id: ctx.itemListId }),
      ...(ctx.itemListName && { item_list_name: ctx.itemListName }),
      ecommerce: {
        currency: 'EUR',
        value,
        items: [ga4Item],
      },
    })
    trackProductFruits('addToCart')
  }

  function trackRemoveFromCart(
    item: Ga4CartItemInput,
    supplier: { name?: string },
    ctx: { index?: number, pageSection?: string } = {},
  ) {
    const index = ctx.index ?? 1
    const ga4Item = convertCartItemToGa4EcommerceItem(item, supplier.name ?? '', index, gpoContext())
    ga4Item.price = getItemPrice(item as unknown as Parameters<typeof getItemPrice>[0]) / 100
    const value = Math.round((ga4Item.price ?? 0) * (ga4Item.quantity ?? 0) * 100) / 100

    gtm?.trackEvent({ ecommerce: null })
    gtm?.trackEvent({
      event: 'remove_from_cart',
      page_section: ctx.pageSection || currentPageSection(),
      ecommerce: {
        currency: 'EUR',
        value,
        items: [ga4Item],
      },
    })
  }

  function trackViewCart(carts: CartLike[]) {
    const distinctHandles = new Set<string>()
    const items: Ga4CartItem[] = []
    let value = 0
    let runningIndex = 1

    for (const cart of carts) {
      const supplierName = cart.supplier?.name ?? ''
      // calculateCartTotal works on CartV2 shape (cart.items + cart.supplier).
      value += calculateCartTotal(cart as unknown as Parameters<typeof calculateCartTotal>[0])

      for (const cartItem of cart.items) {
        distinctHandles.add(cartItem.handle)
        const ga4Item = convertCartItemToGa4EcommerceItem(cartItem, supplierName, runningIndex, gpoContext())
        ga4Item.price = getItemPrice(cartItem as unknown as Parameters<typeof getItemPrice>[0]) / 100
        items.push(ga4Item)
        runningIndex++
      }
    }

    gtm?.trackEvent({ ecommerce: null })
    gtm?.trackEvent({
      event: 'view_cart',
      page_section: getOriginSection() ?? currentPageSection(),
      supplier_count: carts.length,
      supplier_names: carts.map(c => c.supplier?.name ?? ''),
      distinct_item_count: distinctHandles.size,
      ecommerce: {
        currency: 'EUR',
        value: value / 100,
        items,
      },
    })
  }

  function trackBeginCheckout(carts: CartLike[], allCarts: CartLike[], ctx: BeginCheckoutCtx) {
    const items: Ga4CartItem[] = []
    const distinctHandles = new Set<string>()
    let value = 0
    let shipping = 0
    let runningIndex = 1

    for (const cart of carts) {
      const supplierName = cart.supplier?.name ?? ''
      // calculateCartTotal + getTotalFreightCost both operate on the CartV2 shape
      // (cart.items + cart.supplier).
      const cartShape = cart as unknown as Parameters<typeof calculateCartTotal>[0]
      value += calculateCartTotal(cartShape)
      shipping += getTotalFreightCost(cartShape)

      for (const cartItem of cart.items) {
        distinctHandles.add(cartItem.handle)
        const ga4Item = convertCartItemToGa4EcommerceItem(cartItem, supplierName, runningIndex, gpoContext())
        ga4Item.price = getItemPrice(cartItem as unknown as Parameters<typeof getItemPrice>[0]) / 100
        items.push(ga4Item)
        runningIndex++
      }
    }

    gtm?.trackEvent({ ecommerce: null })
    gtm?.trackEvent({
      event: 'begin_checkout',
      page_section: ctx.pageSection || currentPageSection(),
      checkout_type: ctx.checkoutType,
      // Report every supplier in the cart, not only the ones being checked out
      // (holds for single and combined checkouts too).
      supplier_count: allCarts.length,
      supplier_names: allCarts.map(c => c.supplier?.name ?? ''),
      // Items/value/distinct_item_count remain scoped to this checkout.
      distinct_item_count: distinctHandles.size,
      ecommerce: {
        currency: 'EUR',
        value: value / 100,
        shipping: shipping / 100,
        items,
      },
    })
  }

  function trackPurchase(carts: CartLike[], ctx: PurchaseCtx) {
    const items: Ga4CartItem[] = []
    const distinctHandles = new Set<string>()
    let value = 0
    let shipping = 0
    // GA4 `index` is 1-based (position in the item list).
    let runningIndex = 1

    for (const cart of carts) {
      const supplierName = cart.supplier?.name ?? ''
      const cartShape = cart as unknown as Parameters<typeof calculateCartTotal>[0]
      value += calculateCartTotal(cartShape)
      shipping += getTotalFreightCost(cartShape)

      for (const cartItem of cart.items) {
        distinctHandles.add(cartItem.handle)
        const ga4Item = convertCartItemToGa4EcommerceItem(cartItem, supplierName, runningIndex, gpoContext())
        // Reflect the real unit price (scale/campaign), matching the event-level value.
        ga4Item.price = getItemPrice(cartItem as unknown as Parameters<typeof getItemPrice>[0]) / 100
        items.push(ga4Item)
        runningIndex++
      }
    }

    // Collect unique suppliers from the purchased carts only.
    const supplierHandles = [...new Set(carts.map(c => c.supplier?.handle).filter((h): h is string => Boolean(h)))]
    const supplierNames = supplierHandles.map(handle => carts.find(c => c.supplier?.handle === handle)?.supplier?.name ?? '')

    gtm?.trackEvent({ ecommerce: null })
    gtm?.trackEvent({
      event: 'purchase',
      customer_type: ctx.customerType,
      supplier_count: supplierHandles.length,
      supplier_names: supplierNames,
      // Items/value/distinct_item_count remain scoped to the submitted carts.
      distinct_item_count: distinctHandles.size,
      ecommerce: {
        transaction_id: ctx.transactionId,
        currency: 'EUR',
        value: value / 100,
        coupon: ctx.coupon ?? '',
        shipping: shipping / 100,
        items,
      },
    })
  }

  function trackClickCartSuggestionAdd(item: Ga4CartItemInput, ctx: SuggestionTrackingCtx = {}) {
    const index = ctx.index ?? 1
    // Reflect the real unit price (scale/GPO/campaign), matching add_to_cart.
    const price = getItemPrice(item as unknown as Parameters<typeof getItemPrice>[0]) / 100
    const value = Math.round(price * (item.quantity ?? 1) * 100) / 100
    const promotion: 'yes' | 'no' = item.campaign?.status === 'ACTIVE' ? 'yes' : 'no'
    const gpoRecommended: 'yes' | 'no' = gpoContext().isGpoCompliant(item) ? 'yes' : 'no'

    gtm?.trackEvent({ ecommerce: null })
    gtm?.trackEvent({
      event: 'click_cart_suggestion_add',
      ecommerce: {
        currency: 'EUR',
        value,
        items: [{
          item_id: item.handle,
          item_name: item.name || 'Unknown',
          price,
          promotion,
          index,
          gpo_recommended: gpoRecommended,
        }],
      },
    })
  }

  function trackExpandCartSummary(cart: CartLike) {
    const supplierName = cart.supplier?.name ?? ''
    const items: Ga4CartItem[] = []
    let valueCents = 0
    let runningIndex = 1

    for (const cartItem of cart.items) {
      const ga4Item = convertCartItemToGa4EcommerceItem(cartItem, supplierName, runningIndex, gpoContext())
      ga4Item.price = getItemPrice(cartItem as unknown as Parameters<typeof getItemPrice>[0]) / 100
      valueCents += getItemPrice(cartItem as unknown as Parameters<typeof getItemPrice>[0]) * (ga4Item.quantity ?? 0)
      items.push(ga4Item)
      runningIndex++
    }
    const value = Math.round(valueCents) / 100

    gtm?.trackEvent({ ecommerce: null })
    gtm?.trackEvent({
      event: 'expand_cart_summary',
      page_section: currentPageSection(),
      supplier_name: supplierName,
      ecommerce: {
        currency: 'EUR',
        value,
        items,
      },
    })
  }

  /**
   * Resolves the GA4 payload for the two `GPO_recommendations_*` events.
   */
  function resolveGpoRecommendationsPayload(
    bindings: GpoRecommendationsBindings,
  ): GpoRecommendationsPayload | undefined {
    if (!userClinicChainName.value) return undefined
    const supplierHandle = toValue(bindings.supplierHandle)
    const supplierName = cartStore.carts.find(c => c.supplier?.handle === supplierHandle)?.supplier?.name
    if (!supplierName) return undefined
    return {
      gpoName: userClinicChainName.value,
      itemId: toValue(bindings.itemId),
      itemName: toValue(bindings.itemName),
      supplierName,
      supplierHandle,
    }
  }

  function emitGpoRecommendationsEvent(
    eventName: 'GPO_recommendations_view' | 'GPO_recommendations_start',
    payload: GpoRecommendationsPayload,
  ) {
    gtm?.trackEvent({
      event: eventName,
      gpo_name: payload.gpoName,
      item_id: payload.itemId,
      item_name: payload.itemName,
      supplier_name: payload.supplierName,
    })
  }

  /**
   * Observes `target` and fires `GPO_recommendations_view` (deduped via the
   * module-scoped `firedGpoViews`) the first time the element becomes visible
   * — provided the CTA preconditions are met (alternatives exist, user is on a
   * non-matching supplier, has a GPO).
   *
   * MUST be called during `setup()` of a component so the underlying
   * `useIntersectionObserver` can register its lifecycle hooks.
   */
  function trackGpoRecommendationsViewOnVisible(
    target: MaybeComputedElementRef,
    bindings: GpoRecommendationsViewBindings,
  ) {
    useGtmVisibilityTracking(target, {
      currentSlide: ref(0),
      buildPayload: () => {
        if (!toValue(bindings.hasAlternativeProducts) || toValue(bindings.matchingClinicChain)) {
          return undefined
        }
        const payload = resolveGpoRecommendationsPayload(bindings)
        if (!payload) return undefined

        const dedupeKey = `${payload.supplierHandle}:${payload.itemId}`
        if (firedGpoViews.has(dedupeKey)) return undefined
        firedGpoViews.add(dedupeKey)

        emitGpoRecommendationsEvent('GPO_recommendations_view', payload)
        return undefined
      },
    })
  }

  function trackGpoRecommendationsStart(bindings: GpoRecommendationsBindings) {
    const payload = resolveGpoRecommendationsPayload(bindings)
    if (!payload) return

    emitGpoRecommendationsEvent('GPO_recommendations_start', payload)
  }

  function resetGpoViewDedupe() {
    firedGpoViews.clear()
  }

  return {
    trackAddToCart,
    trackRemoveFromCart,
    trackViewCart,
    trackBeginCheckout,
    trackPurchase,
    trackClickCartSuggestionAdd,
    trackExpandCartSummary,
    trackGpoRecommendationsViewOnVisible,
    trackGpoRecommendationsStart,
    resetGpoViewDedupe,
  }
}
